import type { NMPLocale } from "@borealis/locale-common";
import { i18n } from "@lingui/core";
import React from "react";
import { SemanticFilter } from "src/types/SemanticFilter";
import { RangeInput } from "./RangeInput";

type RangeFormProps = {
	filter: SemanticFilter;
	locale: NMPLocale;
	min: number;
	max: number;
	minLabel: string;
	maxLabel: string;
	minValue?: number;
	maxValue?: number;
	onChange?: (values: [number | undefined, number | undefined]) => void;
};

export function RangeForm(props: RangeFormProps) {
	const { filter, locale, min, max, minLabel, maxLabel, minValue, maxValue, onChange } = props;

	// Check if range is invalid (min > max)
	const isInvalid = minValue !== undefined && maxValue !== undefined && minValue > maxValue;

	// Create a reusable handler for both min and max inputs
	const createRangeChangeHandler = React.useCallback(
		(isMin: boolean) => (_name: string, value: string) => {
			const digitsOnly = value.replace(/[^\d]/g, "");

			// Handle empty input - clear the value
			if (digitsOnly === "") {
				onChange?.(isMin ? [undefined, maxValue] : [minValue, undefined]);
				return;
			}

			// Parse and validate the input
			const parsed = Number(digitsOnly);
			if (!Number.isFinite(parsed)) return;

			// Clamp value to filter bounds
			const clamped = Math.min(Math.max(min, parsed), max);
			onChange?.(isMin ? [clamped, maxValue] : [minValue, clamped]);
		},
		[min, max, minValue, maxValue, onChange],
	);

	const handleMinChange = React.useMemo(() => createRangeChangeHandler(true), [createRangeChangeHandler]);
	const handleMaxChange = React.useMemo(() => createRangeChangeHandler(false), [createRangeChangeHandler]);

	const handleMinBlur = React.useCallback(() => {
		// Apply crossover logic on blur: if min exceeds max, adjust max
		if (minValue !== undefined && maxValue !== undefined && minValue > maxValue) {
			onChange?.([minValue, minValue]);
		}
	}, [minValue, maxValue, onChange]);

	const handleMaxBlur = React.useCallback(() => {
		// Apply crossover logic on blur: if max is below min, adjust min
		if (minValue !== undefined && maxValue !== undefined && maxValue < minValue) {
			onChange?.([maxValue, maxValue]);
		}
	}, [minValue, maxValue, onChange]);

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-12">
				<RangeInput
					ariaLabel={i18n.t({
						id: "filters.range.minimum.label",
						message: "{filterName}, minimum",
						values: { filterName: filter.display_name },
					})}
					invalid={isInvalid}
					label={minLabel}
					locale={locale}
					name="min"
					onChange={handleMinChange}
					onBlur={handleMinBlur}
					value={minValue?.toString() ?? ""}
				/>
				<RangeInput
					ariaLabel={i18n.t({
						id: "filters.range.maximum.label",
						message: "{filterName}, maximum",
						values: { filterName: filter.display_name },
					})}
					invalid={isInvalid}
					label={maxLabel}
					locale={locale}
					name="max"
					onChange={handleMaxChange}
					onBlur={handleMaxBlur}
					value={maxValue?.toString() ?? ""}
				/>
			</div>
			{isInvalid && (
				<div className="text-xs s-text-negative">
					{i18n.t({
						id: "explore.filter.range.invalid",
						message: "The maximum value can't be smaller than the minimum value.",
					})}
				</div>
			)}
		</div>
	);
}
